// ============================================
// プレイヤーエンティティの制御と行動
// ============================================

import { CONFIG } from '../../config/game-config';
import {
    getSelectedShip,
    getPlayer,
    getPowerLevel,
    getHomingActive,
    getBullets,
    getHighScore,
    setHighScore,
    setDeathAnimTimer,
    setScreenShake,
    getScore,
    getPlayerStateMachine,
    getLives,
    setLives
} from '../../store/index';
import { useBomb } from '../abilities/bomb';
import { useHoming } from '../abilities/homing';
import { useCannon } from '../abilities/cannon';
import { createPlayerDeathEffect } from '../particles/particles';
import { getP5 } from '../../core/p5-instance';
import type { ShipType, MissilePersonality } from '../../types/game-types';
import { DyingState } from './states/player-states';

// ============================================
// shoot() - プレイヤーの射撃
// ============================================
function shoot(): void {
    const p = getP5();
    const selectedShip = getSelectedShip();
    const player = getPlayer();
    if (!player) {
        return;
    }

    const powerLevel = getPowerLevel();
    const homingActive = getHomingActive();
    const bullets = getBullets();

    // LANCERは貫通レーザー（弾数ではなくレーザー幅とダメージ間隔が変化）
    if (selectedShip === 'lancer') {
        const lancerCfg = CONFIG.ships.lancer;
        const laserWidth = 5 + (powerLevel - 1) * 5; // Lv1:5, Lv2:10, Lv3:15, Lv4:20
        const lvIndex = p.constrain(powerLevel - 1, 0, lancerCfg.laserHitCooldownByPower.length - 1);
        const hitCooldown = lancerCfg.laserHitCooldownByPower[lvIndex];

        bullets.push({
            x: player.x,
            y: player.y - player.size / 2,
            vx: 0,
            vy: 0,
            size: 0,
            color: p.color(200, 50, 255),
            damage: lancerCfg.laserDamage,
            trail: [],
            isLaser: true,
            laserWidth: laserWidth,
            penetrating: true,
            hitCooldown: hitCooldown
        });
        return;
    }

    // BOMBER / HUNTER 共通処理
    const shipCfg = CONFIG.ships[selectedShip];
    let effectivePower = powerLevel;

    // HUNTERのホーミング発動中はパワーレベル+1
    if (homingActive && selectedShip === 'hunter') {
        const hunterCfg = CONFIG.ships.hunter;
        effectivePower = p.min(powerLevel + hunterCfg.homingPowerBonus, hunterCfg.powerMaxLevel + 1);
    }

    // 弾数 = baseBulletCount + effectivePower
    const bulletCount = shipCfg.baseBulletCount + effectivePower;
    const bulletDamage = shipCfg.bulletDamage;
    const spread = 15;

    for (let i = 0; i < bulletCount; i++) {
        const offset = (i - (bulletCount - 1) / 2) * spread;

        if (homingActive && selectedShip === 'hunter') {
            // HUNTERホーミング弾 (3フェーズシステム)
            const homingCfg = CONFIG.ships.hunter.homingByPower;
            const lvIndex = p.constrain(powerLevel - 1, 0, 3);
            const bulletSpeed = homingCfg.bulletSpeed[lvIndex];

            // ランダム発射順序を計算（初回ループ時のみ）
            if (i === 0) {
                // Fisher-Yatesシャッフルで発射順序をランダム化
                const indices = Array.from({ length: bulletCount }, (_, idx) => idx);
                for (let j = indices.length - 1; j > 0; j--) {
                    const k = Math.floor(p.random(j + 1));
                    [indices[j], indices[k]] = [indices[k], indices[j]];
                }
                // 累積遅延を計算（ランダム間隔）
                const delays: number[] = [];
                let accumulatedDelay = 0;
                for (let j = 0; j < bulletCount; j++) {
                    delays.push(accumulatedDelay);
                    accumulatedDelay += p.random(homingCfg.launchDelayMin, homingCfg.launchDelayMax);
                }
                // 一時的にplayerオブジェクトに保存（ループ内でアクセス）
                (player as { _homingLaunchOrder?: number[]; _homingDelays?: number[] })._homingLaunchOrder = indices;
                (player as { _homingLaunchOrder?: number[]; _homingDelays?: number[] })._homingDelays = delays;
            }

            // ランダム発射順序と遅延を取得
            const launchOrder = (player as { _homingLaunchOrder?: number[] })._homingLaunchOrder ?? [];
            const delays = (player as { _homingDelays?: number[] })._homingDelays ?? [];
            const launchPosition = launchOrder.indexOf(i);  // この弾は何番目に発射されるか

            // 拡散角度を計算（弾ごとに異なる角度 + ランダム性）
            const spreadRange = homingCfg.spreadAngleRange;
            const baseSpreadAngle = p.map(i, 0, bulletCount - 1, -spreadRange, spreadRange);
            const randomOffset = p.random(-0.15, 0.15);  // ±約8.6度のランダム
            const spreadAngle = baseSpreadAngle + randomOffset;
            const initialAngle = -p.HALF_PI + spreadAngle; // 上方向を基準に拡散

            // 初速はbulletSpeed * 0.8
            const initialSpeed = bulletSpeed * 0.8;

            // ミサイル性格をランダムで決定
            const rand = p.random();
            const dist = homingCfg.personality.distribution;
            let personality: MissilePersonality;
            if (rand < dist[0]) {
                personality = 'baka';
            } else if (rand < dist[0] + dist[1]) {
                personality = 'majime';
            } else {
                personality = 'tensai';
            }

            // 性格に応じたフェーズ時間倍率を取得
            const personalityCfg = homingCfg.personality[personality];
            const phaseMult = personalityCfg.phaseDurationMult;

            bullets.push({
                x: player.x + offset,
                y: player.y,
                vx: p.cos(initialAngle) * initialSpeed,
                vy: p.sin(initialAngle) * initialSpeed,
                size: 0,
                color: p.color(255, 200, 50),
                damage: bulletDamage,
                trail: [],
                isHoming: true,
                target: null,
                homingSpeed: bulletSpeed,
                homingTrailLength: homingCfg.trailLength[lvIndex],
                homingGlowSize: homingCfg.glowSize[lvIndex],
                // 3フェーズ用プロパティ（性格に応じた時間倍率を適用）
                homingPhase: 'spread',
                homingPhaseTimer: homingCfg.spreadDuration[lvIndex] * phaseMult,
                homingInitialAngle: initialAngle,
                // 遅延発射用プロパティ（ランダム順序・ランダム間隔）
                homingDelay: delays[launchPosition] ?? 0,
                homingLaunchActive: launchPosition === 0,  // 最初に発射される弾だけ即アクティブ
                // ミサイル性格
                missilePersonality: personality
            });
        } else {
            // BOMBER / HUNTER 通常弾
            const bulletColor = selectedShip === 'hunter' ? p.color(220, 140, 60) : p.color(0, 255, 255);
            bullets.push({
                x: player.x + offset,
                y: player.y - player.size / 2,
                vx: 0,
                vy: 0,
                size: 0,
                color: bulletColor,
                damage: bulletDamage,
                trail: [],
                isHunterBullet: selectedShip === 'hunter'
            });
        }
    }
}

// ============================================
// triggerPlayerDeath() - プレイヤー死亡処理
// ============================================
function triggerPlayerDeath(): void {
    const player = getPlayer();
    if (!player) {
        return;
    }

    // 既にDying状態なら二重実行を防止
    if (getPlayerStateMachine().getCurrentState()?.name === 'Dying') {
        return;
    }

    const score = getScore();
    const highScore = getHighScore();

    setScreenShake(35);
    createPlayerDeathEffect(player.x, player.y);
    setLives(getLives() - 1);
    if (score > highScore) {
        setHighScore(score);
    }

    // 残機あり: フェード演出分（fadeDelay+FADE_FRAMES=68）+ リスポーン余裕で88F
    // 残機なし: ゲームオーバー演出用に90F
    const DEATH_ANIM_WITH_LIVES = 88;
    const DEATH_ANIM_GAME_OVER = 90;
    setDeathAnimTimer(getLives() > 0 ? DEATH_ANIM_WITH_LIVES : DEATH_ANIM_GAME_OVER);
    getPlayerStateMachine().transitionTo(new DyingState());
}

// ============================================
// useSpecialAbility() - 特殊兵装の使用
// ============================================
function useSpecialAbility(): void {
    const selectedShip: ShipType = getSelectedShip();

    if (selectedShip === 'bomber') {
        useBomb();
    } else if (selectedShip === 'hunter') {
        useHoming();
    } else if (selectedShip === 'lancer') {
        useCannon();
    }
}

export { shoot, triggerPlayerDeath, useSpecialAbility };
